import { addToast } from "@app/components/toast/toast.service";
import { PathnameUrl } from "@app/constants";
import AccountService from "@app/services/http/account.service";
import SocketService from "@app/services/socket.service";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import useObservable from "@core/hooks/use-observable.hook";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Subscription } from "rxjs";

type Step = "WAITING" | "SUCCESS";

export default function PhoneVerificationProcess() {
  const [step, setStep] = useState<Step>("WAITING");
  // Đếm ngược 5 phút (300 giây)
  const [timeLeft, setTimeLeft] = useState<number>(268); // 04:28
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [userPhone, setUserPhone] = useState("");
  const [otpVerifyPhone, setOtpVerifyPhone] = useState("");
  const [timeOtpVerifyPhone, setTimeOtpVerifyPhone] = useState(0);

  const { subscribeUntilDestroy, subscribeOnce } = useObservable();
  const navigate = useNavigate();
  const [update, forceUpdate] = useForceUpdate();

  const receiverPhone = "0902614567";
  const onBack = () => {
    navigate(-1);
  };
  const onClose = () => {
    navigate(PathnameUrl.PROFILE);
  };
  const onResendCode = () => {};

  //socket nhận thông báo lỗi
  const subscription = new Subscription();

  useEffect(() => {
    const sub = SocketService.onVerifyPhoneResponse().subscribe((data) => {
      addToast({ text: data.toString() });

      setStep("SUCCESS");
    });

    subscription.add(sub);
  }, [subscription]);

  useEffect(() => () => subscription.unsubscribe(), [subscription]);

  // Lấy thông tin User hiện tại từ hệ thống
  useEffect(() => {
    subscribeUntilDestroy(AccountService.verifyPhone(), (res) => {
      if (res) {
        setUserPhone(res.phone);
        setOtpVerifyPhone(res.otpVerifyPhone);
        setTimeLeft(
          Math.floor(5 * 60 - (Date.now() - res.timeOtpVerifyPhone) / 1000)
        );
      }
    });
  }, [subscribeUntilDestroy, update]);

  // Đếm ngược thời gian ở Bước 2
  useEffect(() => {
    if (timeLeft <= 0) {
      // navigate(PathnameUrl.VERIFY_PHONE_CONSENT);
      forceUpdate();
    }

    if (step !== "WAITING" || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [step, timeLeft]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const handleCopy = (text: string, fieldName: string) => {
    void navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleResend = () => {
    setTimeLeft(300);

    if (onResendCode) onResendCode();
  };

  const handleCompleteSuccess = () => {
    setStep("SUCCESS");
  };

  return (
    <div className="min-h-screen bg-slate-100/70 flex items-center justify-center p-4 sm:p-8">
      {/* Container chính: Tăng max-width và padding để giao diện to, rõ ràng */}
      <div className="bg-white rounded-3xl shadow-xl border border-slate-200/60 max-w-xl w-full p-8 md:p-10 transition-all">
        {/* ==================== BƯỚC 2: ĐANG CHỜ XÁC THỰC ==================== */}
        {step === "WAITING" && (
          <div>
            {/* Header: Nút Quay lại & Đồng hồ đếm ngược */}
            <div className="flex items-center justify-between pb-6 border-b border-slate-100">
              <button
                type="button"
                onClick={onBack}
                className="text-base font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                ← Quay lại
              </button>

              <div className="inline-flex items-center gap-2 bg-amber-50/80 border border-amber-200/80 px-4 py-2 rounded-full text-amber-600 font-extrabold text-base sm:text-lg shadow-sm">
                <span className="text-lg">⏱</span>
                <span>{formatTime(timeLeft)}</span>
              </div>
            </div>

            {/* Hướng dẫn gửi tin nhắn */}
            <div className="my-6 text-center">
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                Dùng chính số{" "}
                <strong className="text-slate-900 font-bold">
                  {userPhone}
                </strong>{" "}
                đã đăng ký, nhắn tin với nội dung bên dưới tới số:
              </p>
            </div>

            {/* Card 1: Nội dung tin nhắn */}
            <div className="bg-blue-50/40 border border-blue-100 rounded-2xl p-5 md:p-6 mb-5 flex items-center justify-between shadow-xs">
              <div>
                <div className="text-xs sm:text-sm font-bold uppercase tracking-wider text-slate-400 mb-1">
                  NỘI DUNG TIN NHẮN
                </div>
                <span className="text-3xl sm:text-4xl font-black tracking-widest text-blue-600">
                  {otpVerifyPhone}
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleCopy(otpVerifyPhone, "code")}
                className="inline-flex items-center gap-1.5 text-sm sm:text-base font-semibold text-blue-600 bg-white border border-slate-200 px-4 py-2.5 rounded-xl hover:bg-blue-50 hover:border-blue-200 transition-all shadow-sm cursor-pointer shrink-0"
              >
                <span>📋</span>
                <span>
                  {copiedField === "code" ? "Đã sao chép!" : "Sao chép"}
                </span>
              </button>
            </div>

            {/* Card 2: Số nhận tin nhắn */}
            <div className="bg-slate-50/80 border border-slate-200/80 rounded-2xl p-5 md:p-6 mb-6 flex items-center justify-between shadow-xs">
              <div className="flex items-center gap-4">
                <div className="text-2xl sm:text-3xl">📞</div>
                <div>
                  <div className="text-xs sm:text-sm font-bold uppercase tracking-wider text-slate-400 mb-0.5">
                    SỐ NHẬN TIN NHẮN
                  </div>
                  <span className="text-lg sm:text-2xl font-bold text-slate-800">
                    {receiverPhone}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleCopy(receiverPhone, "phone")}
                className="inline-flex items-center gap-1.5 text-sm sm:text-base font-semibold text-blue-600 bg-white border border-slate-200 px-4 py-2.5 rounded-xl hover:bg-blue-50 hover:border-blue-200 transition-all shadow-sm cursor-pointer shrink-0"
              >
                <span>📋</span>
                <span>
                  {copiedField === "phone" ? "Đã sao chép!" : "Sao chép"}
                </span>
              </button>
            </div>

            {/* Card 3: Lưu ý quan trọng */}
            <div className="bg-amber-50/60 border border-amber-200/70 rounded-2xl p-5 md:p-6 mb-6 text-amber-900">
              <div className="font-bold text-base sm:text-lg mb-3 flex items-center gap-2 text-amber-800">
                <span>⚠️</span>
                <span>Lưu ý quan trọng</span>
              </div>
              <ul className="space-y-2 text-sm sm:text-base text-amber-800/90 leading-relaxed pl-2">
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 font-bold">•</span>
                  <span>Chỉ dùng đúng số điện thoại đã đăng ký.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 font-bold">•</span>
                  <span>Nhắn chính xác nội dung mã xác thực.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 font-bold">•</span>
                  <span>Không thêm ký tự đặc biệt hoặc khoảng trắng.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 font-bold">•</span>
                  <span>
                    Sau khi chúng tôi nhận được tin nhắn có nội dung{" "}
                    <span className="text-blue-600 font-bold">6 số trên</span>,
                    quá trình xác minh sẽ hoàn tất.
                  </span>
                </li>
              </ul>
            </div>

            {/* Gửi lại mã */}
            {/* <div className="flex items-center justify-between text-sm sm:text-base text-slate-500 pt-2 pb-6 border-b border-slate-100">
              <span>Chưa nhận được tin nhắn?</span>
              <button
                type="button"
                onClick={handleResend}
                className="text-blue-600 font-bold hover:underline cursor-pointer"
              >
                Gửi lại mã mới
              </button>
            </div> */}

            {/* Nút giả lập thành công */}
            {/* <div className="pt-6">
              <button
                type="button"
                onClick={handleCompleteSuccess}
                className="w-full bg-[#1e293b] hover:bg-[#0f172a] text-white font-bold text-base sm:text-lg py-4 px-6 rounded-2xl transition-all shadow-md active:scale-[0.99] cursor-pointer"
              >
                (Demo) Giả lập khớp mã SMS thành công
              </button>
            </div> */}
          </div>
        )}

        {/* ==================== BƯỚC 3: XÁC THỰC THÀNH CÔNG ==================== */}
        {step === "SUCCESS" && (
          <div className="text-center py-6 sm:py-8 space-y-6">
            {/* Icon tích xanh to tròn có bóng mờ */}
            <div className="relative w-24 h-24 sm:w-28 sm:h-28 bg-emerald-500 text-white rounded-full flex items-center justify-center text-4xl sm:text-5xl mx-auto shadow-xl shadow-emerald-200 animate-bounce-short">
              ✓
            </div>

            {/* Thông báo */}
            <div className="space-y-3">
              <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                Xác thực thành công!
              </h3>
              <p className="text-base sm:text-lg text-slate-600 max-w-sm mx-auto leading-relaxed">
                Số điện thoại{" "}
                <strong className="text-slate-900 font-bold">
                  {userPhone}
                </strong>{" "}
                đã được xác thực thành công.
              </p>
            </div>

            {/* Nút Đóng */}
            <div className="pt-4">
              <button
                type="button"
                onClick={onClose}
                className="w-full bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-base sm:text-lg py-4 px-6 rounded-2xl transition-colors border border-slate-200/80 cursor-pointer shadow-xs active:scale-[0.99]"
              >
                Đóng
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
